import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { RepSelect, RoleSelect } from "./UserSelects";

const EditForm = ({ u, reps, onSave, onCancel }) => {
  const [name, setName] = useState(u.name);
  const [role, setRole] = useState(u.role);
  const [repId, setRepId] = useState(u.rep_id || "");
  const [error, setError] = useState("");
  const save = () => (name.trim() ? onSave({ name: name.trim(), role, rep_id: repId || null }) : setError("Full name is required"));
  return (
    <div className="grid flex-1 gap-2 sm:grid-cols-3">
      <label className="block"><span className="field-label">Full name</span>
        <input data-testid={`users-edit-name-input-${u.id}`} value={name} onChange={(e) => { setName(e.target.value); setError(""); }} className={`inp ${error ? "inp-invalid" : ""}`} />
        {error && <span data-testid={`users-edit-error-${u.id}`} className="field-error mt-1 block">{error}</span>}
      </label>
      <RoleSelect testid={`users-edit-role-select-${u.id}`} value={role} onChange={setRole} />
      <RepSelect testid={`users-edit-rep-select-${u.id}`} reps={reps} value={repId} onChange={setRepId} />
      <div className="flex gap-2 sm:col-span-3">
        <button type="button" data-testid={`users-cancel-btn-${u.id}`} onClick={onCancel} className="btn-ghost">Cancel</button>
        <button type="button" data-testid={`users-save-btn-${u.id}`} onClick={save} className="btn-primary">Save</button>
      </div>
    </div>
  );
};

const ResetForm = ({ u, onDone }) => {
  const [pw, setPw] = useState("");
  const [error, setError] = useState("");
  const submit = async () => {
    if (pw.length < 8) return setError(pw ? "Password must be at least 8 characters" : "Password is required");
    try {
      await api.post(`/users/${u.id}/reset-password`, { password: pw });
      toast.success(`Password reset for ${u.name}`);
      onDone();
    } catch (e) { setError(errMsg(e)); }
  };
  return (
    <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-start" data-testid={`users-reset-form-${u.id}`}>
      <div className="flex-1">
        <input type="password" data-testid={`users-reset-input-${u.id}`} value={pw} placeholder="New password (min 8)" autoComplete="new-password"
          onChange={(e) => { setPw(e.target.value); setError(""); }} className={`inp ${error ? "inp-invalid" : ""}`} />
        {error && <span data-testid={`users-reset-error-${u.id}`} className="field-error mt-1 block">{error}</span>}
      </div>
      <div className="flex gap-2">
        <button type="button" data-testid={`users-reset-cancel-btn-${u.id}`} onClick={onDone} className="btn-ghost">Cancel</button>
        <button type="button" data-testid={`users-reset-submit-btn-${u.id}`} onClick={submit} className="btn-primary">Set password</button>
      </div>
    </div>
  );
};

const UserInfo = ({ u, rep, isMe }) => (
  <div className="flex min-w-0 flex-1 items-center gap-3">
    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-indigo-500/15 font-display text-sm font-bold text-indigo-300">
      {u.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
    </span>
    <div className="min-w-0">
      <p className="truncate font-semibold"><span data-testid={`users-name-${u.id}`}>{u.name}</span>
        {isMe && <span className="sample-chip ml-2">You</span>}</p>
      <p className="break-words text-xs text-slate-400">
        <span data-testid={`users-username-${u.id}`} className="font-mono">@{u.username}</span> · <span data-testid={`users-role-${u.id}`} className={u.role === "Admin" ? "text-indigo-300" : ""}>{u.role}</span>
        {" · "}<span data-testid={`users-rep-${u.id}`}>{rep ? rep.name : "No rep"}</span>
        {" · "}<span data-testid={`users-status-${u.id}`}>{u.active ? "Active" : "Deactivated"}</span>
      </p>
    </div>
  </div>
);

const RowActions = ({ u, patch, setMode }) => (
  <div className="flex flex-wrap gap-2">
    <button type="button" data-testid={`users-edit-btn-${u.id}`} onClick={() => setMode("edit")} className="btn-ghost h-9">Edit</button>
    <button type="button" data-testid={`users-reset-btn-${u.id}`} onClick={() => setMode("reset")} className="btn-subtle">Reset password</button>
    <button type="button" data-testid={`users-admin-toggle-btn-${u.id}`} className="btn-subtle"
      onClick={() => patch({ role: u.role === "Admin" ? "User" : "Admin" }, u.role === "Admin" ? `${u.name} is now a User` : `${u.name} is now an Admin`)}>
      {u.role === "Admin" ? "Remove admin" : "Make admin"}
    </button>
    <button type="button" data-testid={`users-toggle-active-btn-${u.id}`} className="btn-subtle"
      onClick={() => patch({ active: !u.active }, u.active ? `${u.name} deactivated` : `${u.name} reactivated`)}>
      {u.active ? "Deactivate" : "Reactivate"}
    </button>
  </div>
);

export const UserRow = ({ u, reps, rep, isMe, onSaved }) => {
  const [mode, setMode] = useState("view");
  const patch = async (body, msg) => {
    try {
      const { data } = await api.patch(`/users/${u.id}`, body);
      onSaved(data);
      toast.success(msg);
      setMode("view");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <li data-testid={`users-row-${u.id}`} className={`flex flex-col gap-3 px-4 py-3.5 lg:flex-row lg:items-center ${u.active ? "" : "opacity-60"}`}>
      {mode === "edit" && <EditForm u={u} reps={reps} onSave={(b) => patch(b, "User updated")} onCancel={() => setMode("view")} />}
      {mode !== "edit" && <UserInfo u={u} rep={rep} isMe={isMe} />}
      {mode === "view" && <RowActions u={u} patch={patch} setMode={setMode} />}
      {mode === "reset" && <ResetForm u={u} onDone={() => setMode("view")} />}
    </li>
  );
};
